// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject, Subscription, of } from 'rxjs';
import { map, filter } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { User } from 'oidc-client';
import moment from 'moment';
import { cloneDeep, fill, flatten, isNull } from 'lodash';

// --- HS Code
import {
  Constants,
  GroupModel,
  InvitationTypes,
  NotificationModel,
  RespondentEmailValidationModel,
  RespondentModel,
  RespondentProgressModel,
  RespondentStatuses,
  UndoModel,
} from '../../../models';
import { OidcService } from '../account/oidc.service';
import { AppService } from '../app.service';
import { UserNotificationBaseService } from '../notifications/user-notification-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class ParticipantGroupsBaseService {
  private _notificationSubscription: Subscription;
  private _identitySubscription: Subscription;
  private _identity: User | null = null;

  protected _participantGroups$: BehaviorSubject<GroupModel[] | null>;
  get participantGroups$(): Observable<GroupModel[]> {
    return this._participantGroups$.asObservable().pipe(
      filter((g): g is GroupModel[] => !!g),
      map((pGroup): GroupModel[] => {
        return pGroup.map((g) => {
          g.numberOfRespondents = g.respondents.filter(
            (r) => typeof r.status !== 'undefined'
          ).length;
          g.numberOfRespondentsComplete = g.respondents.filter(
            (r) => r.status && r.status === RespondentStatuses.completedSurvey
          ).length;
          g.respondents = g.respondents.map((r) => {
            r.canResendEmail = moment(r.lastEmailSentDate).isBefore(
              moment().subtract(1, 'day')
            );
            return r;
          });
          return g;
        });
      })
    );
  }

  protected _validatedRespondent$: BehaviorSubject<RespondentModel | null>;
  get validatedRespondent$(): Observable<RespondentModel | null> {
    return this._validatedRespondent$;
  }

  protected _areMinimumNumberOfParticipantsInvited$: BehaviorSubject<boolean>;
  get areMinimumNumberOfParticipantsInvited$(): Observable<boolean> {
    return this._areMinimumNumberOfParticipantsInvited$;
  }

  respondentProgress$: Observable<RespondentProgressModel | null>;

  constructor(
    protected translateService: TranslateService,
    protected oidcService: OidcService,
    protected appService: AppService,
    protected userNotificationService: UserNotificationBaseService
  ) {
    this._participantGroups$ = new BehaviorSubject<GroupModel[] | null>(
      null
    );
    this._validatedRespondent$ = new BehaviorSubject<RespondentModel | null>(
      null
    );
    this._areMinimumNumberOfParticipantsInvited$ = new BehaviorSubject<boolean>(
      false
    );

    this._notificationSubscription =
      this.userNotificationService.currentNotification$.subscribe(
        (notification: NotificationModel | null) => {
          if (!notification) {
            return;
          }
          const groups = this._participantGroups$.value;
          if (!groups) {
            return;
          }
          const allRespondents = groups
            .map((g) => g.respondents)
            .reduce(
              (accumulator, currentValue) => accumulator.concat(currentValue),
              []
            );
          for (const respondent of allRespondents) {
            if (
              respondent?.id?.toLowerCase() ===
              notification.identifier.toLowerCase()
            ) {
              respondent.percentageComplete = Math.round(
                (parseInt(notification.body, 10) * 100) /
                  (Constants.NUMBER_OF_INDICATOR_QUESTIONS + 1)
              );
              break;
            }
          }

          this._participantGroups$.next(groups);
        }
      );
    this._identitySubscription = this.oidcService.identity$.subscribe(
      (identity: User | null) => (this._identity = identity)
    );

    this.respondentProgress$ = this.participantGroups$.pipe(
      map((groups): RespondentProgressModel | null => {
        if (!groups) {
          return null;
        }
        const respondents = groups.map((g) =>
          g.respondents.filter((r) => typeof r.status !== 'undefined')
        );
        const flattenRespondents = flatten(respondents);
        const numberOfRespondents = flattenRespondents.length;
        const numberOfRespondentsInvited = flattenRespondents.filter((f) => {
          return f.status && f.status >= RespondentStatuses.invited;
        }).length;
        const areAllRespondentsInvited =
          numberOfRespondentsInvited > 0 &&
          numberOfRespondentsInvited >= numberOfRespondents;
        const numberOfRespondentsComplete = flattenRespondents.filter((f) => {
          return f.status && f.status === RespondentStatuses.completedSurvey;
        }).length;
        const incompleteRespondents = flattenRespondents.filter((f) => {
          return (
            f.status &&
            f.status < RespondentStatuses.completedSurvey &&
            f.status >= RespondentStatuses.started
          );
        });
        let cumulativePercentage = 0;
        if (incompleteRespondents && incompleteRespondents.length > 0) {
          const percentages = incompleteRespondents.map(
            (r) => r.percentageComplete
          );
          cumulativePercentage = percentages.reduce((a, r) => a + r);
        }
        cumulativePercentage = Math.round(
          cumulativePercentage / numberOfRespondents
        );
        const percentageComplete = Math.trunc(
          (numberOfRespondentsComplete / numberOfRespondents) * 100 +
            cumulativePercentage
        );
        return {
          areAllRespondentsInvited,
          numberOfRespondents,
          numberOfRespondentsComplete,
          numberOfRespondentsInvited,
          percentageComplete,
        };
      })
    );
  }

  ngOnDestroy(): void {
    if (this._identitySubscription) {
      this._identitySubscription.unsubscribe();
      this._identitySubscription = Subscription.EMPTY;
    }
    if (this._notificationSubscription) {
      this._notificationSubscription.unsubscribe();
      this._notificationSubscription = Subscription.EMPTY;
    }
  }

  abstract add(group: GroupModel): void;
  abstract delete(group: GroupModel): void;
  abstract loadGroups(identifier?: string, isSilent?: boolean, forceRefresh?: boolean): void;
  abstract update(groups: GroupModel[]): void;
  abstract saveGroup(group: GroupModel): void;
  abstract saveGroupChanges(
    deletedGroups: GroupModel[],
    addedGroups: GroupModel[],
    updatedGroups: GroupModel[],
    managedGroups: GroupModel[],
    participantId: string): void;
  abstract updateGroupName(group: GroupModel): void;

  addEmpty(group: GroupModel): void {
    if (this._participantGroups$ && this._participantGroups$.value) {
      const groups = cloneDeep(this._participantGroups$.value);
      for (const groupItem of groups) {
        groupItem.isActiveGroup = false;
      }
      group.isActiveGroup = true;
      groups.push(group);
      this._participantGroups$.next(groups);
    }
  }

  createGroup(participantId: string, numberOfRespondents: number = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP): GroupModel {
    let index = 1;
    let setIsExpanded = false;
    if (this._participantGroups$ && this._participantGroups$.value) {
      index = this._participantGroups$.value.length + 1;
      const firstExpandedIndex = this._participantGroups$.value
        .map((g) => g.respondents)
        .reduce(
          (accumulator, currentValue) => accumulator.concat(currentValue),
          []
        )
        .findIndex((r) => r.isExpanded);
      setIsExpanded = firstExpandedIndex < 0;
    }
    const id = this.appService.createGuid();
    return {
      id,
      groupName: '',
      index,
      participantId,
      isActiveGroup: true,
      isSet: false,
      respondents: this._createEmptyRespondentSet(id, numberOfRespondents),
    } as GroupModel;
  }

  respondentValidated(model: RespondentEmailValidationModel): void {
    const theGroup = this._getGroup(model.groupId);
    if (!theGroup) {
      return;
    }

    const theRespondentIndex = theGroup.respondents.findIndex(
      (r) => r.groupIndex === model.respondentGroupIndex
    );
    const oldRespondent = theGroup.respondents.find(
      (r) => r.groupIndex === model.respondentGroupIndex
    );
    const theRespondent = cloneDeep(oldRespondent);

    if (theRespondent) {
      theRespondent.isValid = model.isValid;
      theRespondent.isSaving = model.isValid;
      theRespondent.emailAddress = model.emailAddress;
      theRespondent.errorMessage = model.errorMessage;
      theRespondent.isValidatingEmailAddress = false;

      if (model.firstName && theRespondent.firstName !== model.firstName) {
        theRespondent.firstName = model.firstName;
      }
      if (model.lastName && theRespondent.lastName !== model.lastName) {
        theRespondent.lastName = model.lastName;
      }

      if (model.avatar) {
        theRespondent.avatar = model.avatar;
      }

      theGroup.respondents[theRespondentIndex] = theRespondent;
      this._validatedRespondent$.next(theRespondent);
    } else {
      this._validatedRespondent$.next(null);
    }
  }

  respondentUpdated(model: RespondentModel): void {
    const theGroup = this._getGroup(model.groupId);
    if (!theGroup) {
      return;
    }

    const theRespondentIndex = theGroup.respondents.findIndex(
      (r) => r.groupIndex === model.groupIndex
    );
    const oldRespondent = theGroup.respondents.find(
      (r) => r.groupIndex === model.groupIndex
    );
    const theRespondent = cloneDeep(oldRespondent);

    if (theRespondent) {
      theRespondent.id = model.id;
      theRespondent.participantId = model.participantId;
      theRespondent.groupId = model.groupId;
      theRespondent.groupIndex = model.groupIndex;
      theRespondent.firstName = model.firstName;
      theRespondent.lastName = model.lastName;
      theRespondent.emailAddress = model.emailAddress;
      theRespondent.locale = model.locale;
      theRespondent.languageId = model.languageId;
      theRespondent.isValid = model.isValid;
      theRespondent.isSet = model.isSet;
      theRespondent.errorMessage = model.errorMessage;
      theRespondent.personalMessage = model.personalMessage;
      theRespondent.isValidatingEmailAddress = model.isValidatingEmailAddress;
      theRespondent.isSaving = false;
      theRespondent.isExpanded = model.isExpanded;
      theRespondent.status = model.status;

      theGroup.respondents[theRespondentIndex] = theRespondent;
      this._validatedRespondent$.next(theRespondent);
    } else {
      this._validatedRespondent$.next(null);
    }

    const setRespondents = theGroup.respondents.filter((r) => r.isSet);
    theGroup.numberOfRespondents = setRespondents.length;

    if (theGroup.index === 1) {
      this._areMinimumNumberOfParticipantsInvited$.next(
        theGroup.isSet &&
          setRespondents.length === Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
      );
    }

    this._participantGroups$.next(cloneDeep(this._participantGroups$.value));
  }

  expandNextRespondent() {
    const groups = cloneDeep(this._participantGroups$.value);
    if (groups === null) return;

    let firstExpanded = false;
    for (const group of groups) {
      for (let i = 0; i < Constants.NUMBER_OF_RESPONDENTS_PER_GROUP; i++) {
        if (!group.respondents[i]) {
          continue;
        }
        if (!firstExpanded && !group.respondents[i].isSet) {
          group.respondents[i].isExpanded = true;
          firstExpanded = true;
        }
      }
    }

    this._participantGroups$.next(groups);
  }

  setActiveGroup(model: RespondentModel) {
    const groups = cloneDeep(this._participantGroups$.value);
    if (groups === null) return;

    for (const group of groups) {
      if (group.id.toLowerCase() === model.groupId.toLowerCase()) {
        group.isActiveGroup = true;
      } else {
        group.isActiveGroup = false;
      }
    }
    this._participantGroups$.next(groups);
  }

  refreshRespondent(model: RespondentModel) {
    const theGroup = this._getGroup(model.groupId);
    if (!theGroup) {
      return;
    }

    const theRespondentIndex = theGroup.respondents.findIndex(
      (r) => r.groupIndex === model.groupIndex
    );

    theGroup.respondents[theRespondentIndex] = model;
    this._participantGroups$.next(cloneDeep(this._participantGroups$.value));
    this._validatedRespondent$.next(cloneDeep(model));
  }

  getRespondentFromGroup(groupId: string, index: number) {
    const theGroup = this._getGroup(groupId);
    if (!theGroup) {
      return;
    }

    return theGroup.respondents.find((r) => r.groupIndex === index);
  }

  validateAddOrUpdateLocally(model: RespondentEmailValidationModel): boolean {
    let isValid = true;
    const allGroups = this._participantGroups$.value;
    if (allGroups === null) return false;

    const allRespondents = new Array<RespondentModel>().concat.apply(
      new Array<RespondentModel>(),
      allGroups.map((g) => g.respondents)
    );
    const found = allRespondents.find(
      (r: RespondentModel) =>
        r.emailAddress.trim() === model.emailAddress.trim() &&
        (r.groupId !== model.groupId ||
          r.groupIndex !== model.respondentGroupIndex)
    );

    if (found) {
      isValid = false;
      model.errorMessage = this.translateService.instant(
        'respondentErrorMessageAlreadyARespondent'
      );
    }

    let emailAddress: string = 'NOT_FOUND';
    if (
      this._identity &&
      this._identity.profile &&
      this._identity.profile['preferred_username']
    ) {
      emailAddress = this._identity.profile['preferred_username'].trim();
    }

    if (!found && this._identity && this._identity.profile) {
      if (model.emailAddress.trim() === emailAddress) {
        isValid = false;
        model.errorMessage = this.translateService.instant(
          'respondentErrorMessageRespondentIsUser'
        );
      }
    }

    return isValid;
  }

  isGroupSet(groupId: string): boolean {
    const group: GroupModel | null = this._getGroup(groupId);

    if (group) {
      return group.isSet;
    }

    return false;
  }

  setRespondentReminderSending(respondentId: string, toggle: boolean = false) {
    const groups = this._participantGroups$.value;
    if (groups === null) return;

    groups.forEach((group) => {
      group.respondents.forEach((respondent) => {
        if (respondent.id === respondentId) {
          respondent.isSendingReminder = toggle;
        }
      });
    });
    this._participantGroups$.next(groups);
  }

  setRespondentLastEmailSentDate(respondentId: string) {
    const groups = this._participantGroups$.value;
    if (groups === null) return;

    groups.forEach((group) => {
      group.respondents.forEach((respondent) => {
        if (respondent.id === respondentId) {
          respondent.lastEmailSentDate = moment().toJSON();
        }
      });
    });
    this._participantGroups$.next(groups);
  }

  setRespondentInvitationType(
    respondentId: string,
    invitationType: InvitationTypes
  ) {
    const groups = this._participantGroups$.value;
    if (groups === null) return;

    groups.forEach((group) => {
      group.respondents.forEach((respondent) => {
        if (respondent.id === respondentId) {
          respondent.invitationType = invitationType;
          if (respondent.status < RespondentStatuses.invited) {
            respondent.status = RespondentStatuses.invited;
            respondent.invitationType = InvitationTypes.respondentInvite;
          }
        }
      });
    });
    this._participantGroups$.next(groups);
  }

  protected _setRespondents(groups: GroupModel[], numberOfRespondentsInGroup: number = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP) {
    let firstExpanded = false;
    for (const group of groups) {
      if (!group.respondents) {
        group.respondents = new Array<RespondentModel>(
          Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
        );
      }

      for (let i = 0; i < numberOfRespondentsInGroup; i++) {
        if (!group.respondents[i]) {
          group.respondents[i] = this._createEmptyRespondentModel(
            group.id,
            i + 1
          );
          if (!firstExpanded && !group.respondents[i].isSet) {
            group.respondents[i].isExpanded = true;
            firstExpanded = true;
          }
        }
      }

      const setRespondents = group.respondents.filter((r) => r.isSet);
      group.numberOfRespondents = setRespondents.length;

      if (group.index === 1) {
        this._areMinimumNumberOfParticipantsInvited$.next(
          group.isSet &&
            setRespondents.length === Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
        );
      }
    }
  }

  private _createEmptyRespondentSet(groupId: string, groupSize: number): RespondentModel[] {
    const respondents = new Array<RespondentModel>();
    for (let i = 0; i < groupSize; i++) {
      respondents.push(this._createEmptyRespondentModel(groupId, i + 1))
    }
    return respondents;
  }

  private _createEmptyRespondentModel(
    groupId: string,
    groupIndex: number,
    isExpanded = false
  ) {
    return {
      emailAddress: '',
      firstName: '',
      lastName: '',
      groupId,
      groupIndex,
      isExpanded,
      languageId: 0,
    } as RespondentModel;
  }

  private _getGroup(groupId: string): GroupModel | null {
    const groups = this._participantGroups$.value;
    if (!groups || groups.length === 0) {
      return null;
    }

    const foundGroup = groups.find(
      (g) => g.id.toLowerCase() === groupId.toLowerCase()
    );

    if (foundGroup) {
      return foundGroup;
    }

    return null;
  }

  protected _toggleRespondentBusy(respondentId: string, isBusy = true) {
    const groups = this._participantGroups$.value;

    if (!groups || groups.length === 0) {
      return;
    }

    for (const group of groups) {
      if (!Array.isArray(group.respondents)) {
        continue;
      }
      for (const respondent of group.respondents) {
        if (respondent.id === respondentId) {
          respondent.isBusy = isBusy;
        }
      }
    }
  }

  clearGroups() {
    this._participantGroups$.next(new Array<GroupModel>());
  }

  refreshGroups(result: GroupModel[]) {
    this._participantGroups$.next(result);
  }
}
